import { lazy, Suspense } from 'react';
import { createBrowserRouter, createHashRouter, Navigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

/** Lazy so desktop never eagerly loads `@stytch/react` via these routes. */
const LoginRoute = lazy(() =>
  import('./login').then((m) => ({ default: m.LoginRoute })),
);
const AuthenticateRoute = lazy(() =>
  import('./authenticate').then((m) => ({ default: m.AuthenticateRoute })),
);
const DesktopAuthRoute = lazy(() =>
  import('./desktop-auth').then((m) => ({ default: m.DesktopAuthRoute })),
);
const CreateOrganizationRoute = lazy(() =>
  import('../components/Admin/CreateOrganizationRoute').then((m) => ({
    default: m.CreateOrganizationRoute,
  })),
);
const OnboardingRoute = lazy(() =>
  import('../components/onboarding/OnboardingRoute').then((m) => ({
    default: m.OnboardingRoute,
  })),
);

function LazyAuthRoute({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <LoadingState label="Loading…" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}
import { ChatView } from '../views/ChatView';
import { AgentsView } from '../views/AgentsView';
import { AgentEditorView } from '../views/AgentEditorView';
import { AgentProcessEditorView } from '../views/AgentProcessEditorView';
import { WorkView, WorkLanding } from '../views/WorkView';
import { TeamBoard } from '../views/work/TeamBoard';
import { ProjectBoard, ProjectTicketBoard } from '../views/work/ProjectBoard';
import { InitiativeRoadmap } from '../views/work/InitiativeRoadmap';
import { TicketDetailPanel } from '../components/work/TicketDetailPanel';
import { WorkspacesView } from '../views/WorkspacesView';
import { WorkspaceDetailView } from '../views/WorkspaceDetailView';
import { SecretsView } from '../views/secrets/SecretsView';
import { SettingsView } from '../components/SettingsView';
import { ReleasesRoute } from '../views/ReleasesView';
import { IntegrationsView } from '../views/IntegrationsView';
import { CatalogDetailView } from '../views/CatalogDetailView';
import { ConnectionDetailView } from '../views/ConnectionDetailView';
import { OrgChartView } from '../views/OrgChartView';
import { SearchView } from '../views/search/SearchView';
import { ArtifactsView } from '../views/ArtifactsView';
import { LiveFeedView } from '../views/LiveFeedView';
import { SupportView } from '../views/SupportView';
import { DocsView } from '../views/DocsView';
import { RouteErrorView } from '../views/RouteErrorView';
import { NotFoundView } from '../views/NotFoundView';

// In the packaged Electron app, the renderer loads via `loadFile(file://...)`
// which sets `window.location.pathname` to the literal filesystem path of
// index.html — a string `createBrowserRouter` cannot match against the route
// table, producing React Router's default 404 ("Hey developer…"). HashRouter
// routes off `window.location.hash` instead and is unaffected by the
// pathname. Web (HTTP/HTTPS) keeps BrowserRouter so its pretty URLs (`/chat`,
// `/login`, etc.) are unchanged.
const useHashRouter =
  typeof window !== 'undefined' && window.location.protocol === 'file:';
const createRouter = useHashRouter ? createHashRouter : createBrowserRouter;

function WorkspacesRoute() {
  return <WorkspacesView />;
}

function WorkspaceDetailRoute() {
  return <WorkspaceDetailView />;
}

export const router = createRouter([
  {
    errorElement: <RouteErrorView chromeless />,
    children: [
      // `/login` — Stytch Discovery entry (`<StytchB2B>`).
      {
        path: '/login',
        element: (
          <LazyAuthRoute>
            <LoginRoute />
          </LazyAuthRoute>
        ),
      },
      // `/authenticate` — mandatory second leg for magic-link / SSO returns.
      {
        path: '/authenticate',
        element: (
          <LazyAuthRoute>
            <AuthenticateRoute />
          </LazyAuthRoute>
        ),
      },
      // `/desktop-auth` — browser-bounce landing for the desktop daemon.
      {
        path: '/desktop-auth',
        element: (
          <LazyAuthRoute>
            <DesktopAuthRoute />
          </LazyAuthRoute>
        ),
      },
      // `/create-organization` — zero-org IST onboarding.
      {
        path: '/create-organization',
        element: (
          <LazyAuthRoute>
            <CreateOrganizationRoute />
          </LazyAuthRoute>
        ),
      },
      // `/onboarding` — payment/complimentary gate.
      {
        path: '/onboarding',
        element: (
          <LazyAuthRoute>
            <OnboardingRoute />
          </LazyAuthRoute>
        ),
      },
    ],
  },
  {
    path: '/',
    element: <MainLayout />,
    children: [
      // Pathless, never `/` itself — `/` would replace MainLayout and lose the chrome.
      {
        errorElement: <RouteErrorView />,
        children: [
          {
            index: true,
            element: <Navigate to="/chat" replace />,
          },
          // The chat route IS the chat, so the shell panel stands down here —
          // exactly one chat surface is mounted at a time (app-shell ADR-020).
          {
            path: 'chat',
            element: <ChatView />,
            handle: { shogunDock: { suppressed: true } },
          },
          {
            path: 'chat/:sessionId',
            element: <ChatView />,
            handle: { shogunDock: { suppressed: true } },
          },
          {
            path: 'agents',
            element: <AgentsView />,
          },
          {
            path: 'agents/new',
            element: <AgentEditorView />,
          },
          // Per-agent editor route (round-3 UX). The unified Agents library
          // (AgentLibrary) navigates here on row click, mirroring the Processes
          // library's /agent-processes/:id pattern. AgentEditorView already reads
          // :personaId from useParams and AgentEditorForm loads it in edit mode.
          {
            path: 'agents/:personaId',
            element: <AgentEditorView />,
          },
          // Agent-process editor. Registered in app-shell
          // so web and desktop both resolve the routes. The library itself now
          // lives as the dev-toggle-gated "Processes" tab inside AgentsView,
          // so the bare `/agent-processes` index redirects there;
          // create/edit stay on their own full-page routes.
          {
            path: 'agent-processes',
            element: <Navigate to="/agents?tab=processes" replace />,
          },
          {
            path: 'agent-processes/new',
            element: <AgentProcessEditorView />,
          },
          {
            path: 'agent-processes/:processId',
            element: <AgentProcessEditorView />,
          },
          {
            path: 'personas',
            element: <Navigate to="/agents" replace />,
          },
          {
            path: 'personas/new',
            element: <Navigate to="/agents/new" replace />,
          },
          {
            path: 'sessions',
            element: <Navigate to="/chat" replace />,
          },
          // team → goal → projects → project → ticket (app-shell ADR-010).
          {
            path: 'work',
            element: <WorkView />,
            // The Work views are agent-mediated: the docked chat is the only way
            // to change them, so the route supplies the placement rather than
            // the user's stored preference (app-shell ADR-016).
            handle: { shogunDock: { mode: 'bottom', forceOpen: true } },
            children: [
              { index: true, element: <WorkLanding /> },
              {
                // Layout for the team URL and its ticket panel so TicketCard's
                // relative `tickets/:id` stays on the team (app-shell ADR-010).
                path: 'teams/:teamId',
                element: <TeamBoard />,
                children: [
                  { path: 'tickets/:ticketId', element: <TicketDetailPanel /> },
                ],
              },
              {
                path: 'teams/:teamId/goals/:goalId',
                children: [
                  { index: true, element: <Navigate to="projects" replace /> },
                  { path: 'roadmap', element: <InitiativeRoadmap /> },
                  {
                    path: 'projects',
                    children: [
                      { index: true, element: <ProjectBoard /> },
                      {
                        path: ':projectId',
                        element: <ProjectTicketBoard />,
                        children: [
                          { path: 'tickets/:ticketId', element: <TicketDetailPanel /> },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // Search lives in `app-shell/views/search/` so both the web app and
          // the desktop Electron app pick it up via the shared shell.
          // Registering it here — not in a web-only router — is what
          // prevents the desktop 404 on the new nav link.
          // Search is agent-mediated: the chat sits BESIDE the result list it
          // is conversing about, and the Search agent is its default
          // (app-shell ADR-016, app-shell ADR-021).
          {
            path: 'search',
            element: <SearchView />,
            handle: { shogunDock: { mode: 'right', forceOpen: true, persona: 'Search' } },
          },
          {
            path: 'artifacts',
            element: <ArtifactsView />,
          },
          {
            path: 'artifacts/:id',
            element: <ArtifactsView />,
          },
          {
            path: 'secrets',
            element: <SecretsView />,
          },
          {
            // A search hit deep-links here (secrets ADR-005); without this route
            // every account_secret result 404s.
            path: 'secrets/:secretId',
            element: <SecretsView />,
          },
          {
            path: 'orgchart',
            element: <OrgChartView />,
            handle: { shogunDock: { mode: 'bottom', forceOpen: true } },
          },
          // In-app documentation site. Registered in the
          // shared router — same reasoning as `/search` above — so the desktop
          // Electron app resolves `/docs` without a 404.
          {
            path: 'docs',
            element: <DocsView />,
          },
          {
            path: 'integrations',
            element: <IntegrationsView />,
          },
          {
            path: 'integrations/catalog/:slug',
            element: <CatalogDetailView />,
          },
          {
            path: 'integrations/connections/:connectionId',
            element: <ConnectionDetailView />,
          },
          {
            path: 'workspaces',
            element: <WorkspacesRoute />,
          },
          {
            path: 'workspaces/:workspaceId',
            element: <WorkspaceDetailRoute />,
          },
          {
            path: 'cloud',
            element: <Navigate to="/workspaces" replace />,
          },
          {
            path: 'settings',
            element: <SettingsView />,
          },
          // Settings → Platform → Releases (feature-flags). Shared
          // router — the desktop Electron app resolves the same route.
          {
            path: 'settings/releases',
            element: <ReleasesRoute />,
          },
          {
            path: 'feed',
            element: <LiveFeedView />,
          },
          {
            path: 'feed/:postId',
            element: <LiveFeedView />,
          },
          {
            path: 'support',
            element: <SupportView />,
            handle: { shogunDock: { mode: 'bottom', forceOpen: true, persona: 'Support' } },
          },
          {
            path: 'support/:id',
            element: <SupportView />,
            handle: { shogunDock: { mode: 'bottom', forceOpen: true, persona: 'Support' } },
          },
          {
            path: 'notifications',
            element: <Navigate to="/feed" replace />,
          },
          {
            path: '*',
            element: <NotFoundView />,
          },
        ],
      },
    ],
  },
]);

let desktopSyncNavigationsInstalled = false;

type SyncNavigationRouter = {
  navigate: (to: never, opts?: never) => unknown;
};

/**
 * Force every `router.navigate` on `target` through React Router's `flushSync`
 * option (NavLink has no opt-in).
 *
 * Even with `RouterProvider` stable and auth as a sibling overlay, Electron
 * leaves RR's default `startTransition` lanes expired-but-uncommitted (hash
 * moves, UI stays put) until a flushSync navigation clears them. Web keeps
 * concurrent transitions.
 */
export function installSyncNavigations<T extends SyncNavigationRouter>(target: T): void {
  const navigate = target.navigate.bind(target) as (to: unknown, opts?: unknown) => unknown;
  // Microtask defer: flushSync must not run inside render/lifecycle (declarative
  // <Navigate> calls navigate there → React error-level warning); a microtask
  // still runs before paint, so it clears the expired lanes and keeps FIFO order.
  target.navigate = ((to: unknown, opts?: Record<string, unknown>) =>
    Promise.resolve().then(() =>
      navigate(to, { ...opts, flushSync: true }),
    )) as T['navigate'];
}

/** Idempotent desktop install against the app singleton — call from desktop `App` once. */
export function installDesktopSyncNavigations(): void {
  if (desktopSyncNavigationsInstalled) return;
  desktopSyncNavigationsInstalled = true;
  installSyncNavigations(router);
}
